import type { IDockviewPanelProps } from "dockview";

import { Box, CircularProgress } from "@mui/material";
import { type ReactElement, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Route, Routes } from "react-router-dom";

import createDockPageRegistry, {
	type DockDetailRoute,
	type DockPageMeta,
	type DockPageRegistry,
	type DockPageRegistryOptions
} from "~/components/dock/dockPageRegistry";
import SentryErrorBoundary from "~/helpers/SentryErrorBoundary";
import useErrorBoundaryPathReset from "~/hooks/useErrorBoundaryPathReset";
import DockPanelContext, { DockPanelKeyContext, DockPanelVisibleContext } from "~/providers/DockPanelContext";
import DockTabTitleContext from "~/providers/DockTabTitleContext";

export type { DockDetailRoute, ResolvedDockPage } from "~/components/dock/dockPageRegistry";

export interface DockPage extends DockPageMeta {
	element: ReactElement;
}

export interface DockShellModel extends DockPageRegistry<DockPage> {
	components: Record<string, React.FunctionComponent<IDockviewPanelProps>>;
}

export type DockShellModelOptions = DockPageRegistryOptions;

const panelFallback = <CircularProgress sx={{ display: "block", m: "2rem auto" }} />;

const createPanelComponent = (page: DockPage) => {
	const PanelComponent = (props: IDockviewPanelProps) => {
		const [isActive, setIsActive] = useState(props.api.isActive);
		const [isVisible, setIsVisible] = useState(props.api.isVisible);
		const [hasActivated, setHasActivated] = useState(props.api.isActive);
		const panelPath = (props.params?.path as string | undefined) ?? page.path;
		const errorReset = useErrorBoundaryPathReset(panelPath);
		const { resetAfterError } = errorReset;
		useEffect(() => {
			const syncActiveState = (nextIsActive: boolean) => {
				setIsActive(nextIsActive);
				if (nextIsActive) setHasActivated(true);
			};
			const disposable = props.api.onDidActiveChange((event) => {
				syncActiveState(event.isActive);
				if (event.isActive) resetAfterError();
			});
			// Dockview may activate a lazily-added panel before this listener subscribes.
			syncActiveState(props.api.isActive);
			return () => disposable.dispose();
		}, [props.api, resetAfterError]);
		useEffect(() => {
			const disposable = props.api.onDidVisibilityChange((event) => setIsVisible(event.isVisible));
			setIsVisible(props.api.isVisible);
			return () => disposable.dispose();
		}, [props.api]);
		const paramsRef = useRef(props.params);
		paramsRef.current = props.params;
		const setTabTitle = useCallback(
			(title: string, tooltip?: string) => {
				props.api.setTitle(title);
				props.api.updateParameters({ ...paramsRef.current, tooltip });
			},
			[props.api]
		);
		const pageContent = (
			<SentryErrorBoundary
				key={errorReset.key}
				onError={errorReset.onError}
				onReset={errorReset.onReset}
				pageTitle={page.title}
				showDialog={false}
			>
				<Suspense fallback={panelFallback}>
					{page.routePattern ? (
						<Routes location={panelPath}>
							<Route element={page.element} path={page.routePattern} />
						</Routes>
					) : (
						page.element
					)}
				</Suspense>
			</SentryErrorBoundary>
		);
		return (
			<DockPanelKeyContext.Provider value={props.api.id}>
				<DockPanelContext.Provider value={isActive}>
					<DockPanelVisibleContext.Provider value={isVisible}>
						<DockTabTitleContext.Provider value={setTabTitle}>
							<Box
								sx={{
									"& > .MuiGrid-container": {
										alignContent: "flex-start",
										alignItems: "stretch",
										marginBottom: 0,
										minHeight: "100%"
									},
									"& > .MuiGrid-container > .MuiGrid-root": {
										display: "flex",
										flexDirection: "column",
										minHeight: 0
									},
									"& > .MuiGrid-container > .MuiGrid-root:has(.ag-root-wrapper)": {
										minHeight: "100%"
									},
									display: "flex",
									flexDirection: "column",
									height: "100%",
									overflowX: "hidden",
									overflowY: "auto",
									scrollbarGutter: "stable",
									width: "100%"
								}}
							>
								{page.lazyMountUntilActive && !hasActivated ? panelFallback : pageContent}
							</Box>
						</DockTabTitleContext.Provider>
					</DockPanelVisibleContext.Provider>
				</DockPanelContext.Provider>
			</DockPanelKeyContext.Provider>
		);
	};
	return PanelComponent;
};

const withPanelComponents = (registry: DockPageRegistry<DockPage>): DockShellModel => ({
	...registry,
	components: Object.fromEntries(Object.values(registry.pagesById).map((page) => [page.id, createPanelComponent(page)]))
});

const elementFor = (page: DockPageMeta, elementsById: Record<string, ReactElement>): ReactElement => {
	const element = elementsById[page.id];
	if (!element) throw new Error(`Dock page "${page.id}" has no element`);
	return element;
};

export const attachDockPageElements = (
	registry: DockPageRegistry,
	elementsById: Record<string, ReactElement>
): DockShellModel => {
	const pagesById = Object.fromEntries(
		Object.values(registry.pagesById).map((page) => [page.id, { ...page, element: elementFor(page, elementsById) }])
	);
	return withPanelComponents({ ...registry, defaultPage: pagesById[registry.defaultPage.id], pagesById });
};

const createDockShellModel = (
	pages: DockPage[],
	defaultPageId: string,
	detailRoutes: DockDetailRoute[] = [],
	options: DockShellModelOptions = {}
): DockShellModel => withPanelComponents(createDockPageRegistry(pages, defaultPageId, detailRoutes, options));

export default createDockShellModel;
